'use client'

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'

import type { ServiceCenterDesktop } from '../hooks/use-service-center-desktop'

/** The tab's phone as the runtime publishes it to the provider's views. */
export type ServiceCenterPhone = {
  desktop: ServiceCenterDesktop
  /** Microphone and notification prompts, taken before going Available. */
  beforeAvailable: () => Promise<string | null>
}

/**
 * What the agent has entered for one call: hold, the escalation form and the
 * wrap-up form. It belongs to the call, not to whichever view shows it, so
 * moving between the Internal dock and the floating phone (each remounts the
 * panel) keeps it.
 */
type CallUi = {
  held: boolean
  escalating: boolean
  escalateTarget: string
  escalateNotes: string
  dispositionId: string
  notes: string
  followUp: string
}

const EMPTY_CALL_UI: CallUi = {
  held: false,
  escalating: false,
  escalateTarget: '',
  escalateNotes: '',
  dispositionId: '',
  notes: '',
  followUp: ''
}

/** CallUi for one call id; see usePhoneState for when it is dropped. */
type CallUiEntry = { callId: string | null; ui: CallUi }

const NO_CALL_UI: CallUiEntry = { callId: null, ui: EMPTY_CALL_UI }

type CallUiValue = {
  entry: CallUiEntry
  update: (callId: string, patch: Partial<CallUi>) => void
}

type DismissalValue = {
  /** The call whose popover the agent closed; it stays closed for that call. */
  dismissedCallId: string | null
  dismissCall: (callId: string) => void
}

// Separate contexts, so typing a wrap-up note re-renders the fields that
// read it, not every view of the phone.
export const PhoneContext = createContext<ServiceCenterPhone | null>(null)
/** A full dock is on screen; the floating phone steps aside meanwhile. */
export const DockContext = createContext<(() => () => void) | null>(null)
const CallUiContext = createContext<CallUiValue | null>(null)
const DismissalContext = createContext<DismissalValue | null>(null)

/**
 * The per-call state ServiceCenterPhoneProvider owns. `settleCall` is called
 * with the call the desktop now shows: entries and the dismissal of any
 * other call are dropped, so a call's notes go with it.
 */
export function usePhoneState(
  /** Share entries with the phone's other windows; off for non-agents. */
  share: boolean
) {
  const [callUi, setCallUi] = useState<CallUiEntry>(NO_CALL_UI)
  const [dismissedCallId, setDismissedCallId] = useState<string | null>(null)
  const apply = useCallback(
    (callId: string, patch: Partial<CallUi>) =>
      setCallUi((current) => {
        const base = current.callId === callId ? current.ui : EMPTY_CALL_UI
        return { callId, ui: { ...base, ...patch } }
      }),
    []
  )
  const publish = useCallUiChannel(share, callUi, apply)
  const update = useCallback(
    (callId: string, patch: Partial<CallUi>) => {
      apply(callId, patch)
      publish(callId, patch)
    },
    [apply, publish]
  )
  const settleCall = useCallback((callId: string | null) => {
    setCallUi((current) =>
      current.callId !== null && current.callId !== callId
        ? NO_CALL_UI
        : current
    )
    setDismissedCallId((current) =>
      current !== null && current !== callId ? null : current
    )
  }, [])
  const callUiValue = useMemo(
    () => ({ entry: callUi, update }),
    [callUi, update]
  )
  const dismissalValue = useMemo(
    () => ({ dismissedCallId, dismissCall: setDismissedCallId }),
    [dismissedCallId]
  )
  return { callUiValue, dismissalValue, settleCall }
}

type CallUiMessage =
  | { type: 'patch'; callId: string; patch: Partial<CallUi> }
  | { type: 'want'; from: string }
  | { type: 'entry'; to: string; entry: CallUiEntry }

const CALL_UI_CHANNEL = 'sc:phone:call-ui'
/** Typed fields are sent once typing pauses, not on every keystroke. */
const TYPED_FIELDS = new Set<keyof CallUi>([
  'notes',
  'escalateNotes',
  'followUp'
])
const TYPING_PAUSE_MS = 300

/**
 * Shares what the agent entered for the call between the phone window and
 * every Atlas tab (phone-window.ts), so Hold pressed in a tab shows as held
 * in the window, and notes typed in one are there in the other. Each copy
 * applies the others' patches. A copy that opens mid-call asks for the
 * current entry and takes an answer only for the call it shows (or before
 * it shows one), so a tab still holding an older call never overwrites the
 * live one. Returns the function that sends a local patch.
 */
function useCallUiChannel(
  share: boolean,
  callUi: CallUiEntry,
  apply: (callId: string, patch: Partial<CallUi>) => void
) {
  const channel = useRef<BroadcastChannel | null>(null)
  const latest = useRef(callUi)
  const typed = useRef<{
    callId: string
    patch: Partial<CallUi>
    timer: ReturnType<typeof setTimeout>
  } | null>(null)
  useEffect(() => {
    latest.current = callUi
  }, [callUi])

  const flushTyped = useCallback(() => {
    const pending = typed.current
    if (!pending) return
    clearTimeout(pending.timer)
    typed.current = null
    channel.current?.postMessage({
      type: 'patch',
      callId: pending.callId,
      patch: pending.patch
    } satisfies CallUiMessage)
  }, [])

  useEffect(() => {
    if (!share || typeof BroadcastChannel === 'undefined') return
    const open = new BroadcastChannel(CALL_UI_CHANNEL)
    const self = crypto.randomUUID()
    channel.current = open
    open.onmessage = (event: MessageEvent<CallUiMessage>) => {
      const message = event.data
      const mine = latest.current.callId
      // A patch for another call (a late one, from a copy behind) would
      // replace this call's entry with that call's; only ours applies.
      if (
        message.type === 'patch' &&
        (mine === null || mine === message.callId)
      )
        apply(message.callId, message.patch)
      else if (message.type === 'want' && mine !== null)
        open.postMessage({
          type: 'entry',
          to: message.from,
          entry: latest.current
        } satisfies CallUiMessage)
      else if (
        message.type === 'entry' &&
        message.to === self &&
        message.entry.callId !== null &&
        (mine === null || mine === message.entry.callId)
      )
        apply(message.entry.callId, message.entry.ui)
    }
    open.postMessage({ type: 'want', from: self } satisfies CallUiMessage)
    return () => {
      flushTyped()
      open.close()
      channel.current = null
    }
  }, [share, apply, flushTyped])

  return useCallback(
    (callId: string, patch: Partial<CallUi>) => {
      if (!channel.current) return
      const keys = Object.keys(patch) as (keyof CallUi)[]
      if (keys.length > 0 && keys.every((key) => TYPED_FIELDS.has(key))) {
        const pending = typed.current
        if (pending && pending.callId !== callId) flushTyped()
        const base = typed.current?.patch ?? {}
        if (typed.current) clearTimeout(typed.current.timer)
        typed.current = {
          callId,
          patch: { ...base, ...patch },
          timer: setTimeout(flushTyped, TYPING_PAUSE_MS)
        }
        return
      }
      // Anything else goes at once, after what was typed before it.
      flushTyped()
      channel.current.postMessage({
        type: 'patch',
        callId,
        patch
      } satisfies CallUiMessage)
    },
    [flushTyped]
  )
}

/**
 * How a phone runtime (the window's, or a tab's mirror) hands its phone to
 * ServiceCenterPhoneProvider: before paint, so a ringing call reaches the
 * views in the same frame, and cleared when the runtime goes.
 */
export function usePublishPhone(
  phone: ServiceCenterPhone | null,
  onChange: (phone: ServiceCenterPhone | null) => void,
  onMounted: (mounted: boolean) => void
): void {
  useLayoutEffect(() => onChange(phone), [onChange, phone])
  useEffect(() => () => onChange(null), [onChange])
  useEffect(() => {
    onMounted(true)
    return () => onMounted(false)
  }, [onMounted])
}

/** Provides what usePhoneState holds to the views. */
export function PhoneStateContexts({
  state,
  children
}: {
  state: ReturnType<typeof usePhoneState>
  children: React.ReactNode
}) {
  return createElement(
    CallUiContext.Provider,
    { value: state.callUiValue },
    createElement(
      DismissalContext.Provider,
      { value: state.dismissalValue },
      children
    )
  )
}

function required<T>(value: T | null, hook: string): T {
  if (value === null)
    throw new Error(`${hook} needs ServiceCenterPhoneProvider above it`)
  return value
}

/** The tab's phone, or null outside ServiceCenterPhoneProvider. */
export function useServiceCenterPhone(): ServiceCenterPhone | null {
  return useContext(PhoneContext)
}

/**
 * Which call's popover the agent closed, shared by every view so a popover
 * closed in one view stays closed in the next.
 */
export function usePhoneDismissal(): DismissalValue {
  return required(useContext(DismissalContext), 'usePhoneDismissal')
}

/**
 * Marks the calling host as the phone's full dock while it is mounted, so
 * the floating phone does not show the same panel twice. A layout effect,
 * so the floating phone is gone before the dock is first painted.
 */
export function useDockHost() {
  const registerDock = useContext(DockContext)
  useLayoutEffect(() => registerDock?.(), [registerDock])
}

/** The agent's entries for `callId`, kept by the provider, and a setter. */
export function useCallUi(
  callId: string | null
): [CallUi, (patch: Partial<CallUi>) => void] {
  const { entry, update } = required(useContext(CallUiContext), 'useCallUi')
  const ui =
    callId !== null && entry.callId === callId ? entry.ui : EMPTY_CALL_UI
  const set = (patch: Partial<CallUi>) => {
    if (callId !== null) update(callId, patch)
  }
  return [ui, set]
}
